// CO2-Wolke über einer Station. Größe folgt den Emissionen, ein Punkte-Nebel zeigt die Unsicherheit.
import * as THREE from "three"
import gsap from "gsap"
import { COLORS } from "../world/palette.js"

const PUFFS = [
  [0, 0, 0, 1.0], [0.9, -0.15, 0.2, 0.75], [-0.85, -0.1, -0.1, 0.8], [0.3, 0.45, -0.3, 0.7],
  [-0.35, 0.35, 0.4, 0.65], [0.4, -0.3, -0.7, 0.6], [-0.5, -0.3, 0.75, 0.55]
]

export class Co2Cloud {
  constructor({ color = COLORS.cloudScope3, height = 9 }) {
    this.group = new THREE.Group()
    this.group.position.y = height
    this.baseHeight = height
    this.baseColor = new THREE.Color(color)
    this.material = new THREE.MeshStandardMaterial({ color, flatShading: true, roughness: 1, transparent: true, opacity: 0.92 })
    this.body = new THREE.Group()
    const geometry = new THREE.IcosahedronGeometry(1, 1)
    for (const [x, y, z, s] of PUFFS) {
      const puff = new THREE.Mesh(geometry, this.material)
      puff.position.set(x * 1.6, y * 1.6, z * 1.6)
      puff.scale.setScalar(s * 1.5)
      puff.castShadow = true
      this.body.add(puff)
    }
    this.group.add(this.body)

    const count = 90
    const positions = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      const v = new THREE.Vector3().randomDirection().multiplyScalar(2.6 + Math.random() * 0.8)
      positions.set([v.x * 1.3, v.y * 0.8, v.z * 1.3], i * 3)
    }
    const haloGeometry = new THREE.BufferGeometry()
    haloGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3))
    this.halo = new THREE.Points(haloGeometry, new THREE.PointsMaterial({ color: COLORS.lime, size: 0.22, transparent: true, opacity: 0.8, depthWrite: false }))
    this.body.add(this.halo)

    this.scale = 0
    this.uncertainty = 0.5
    this.body.scale.setScalar(0.0001)
    this.phase = Math.random() * Math.PI * 2
  }

  setSize(scale, { duration = 1.2, delay = 0 } = {}) {
    this.scale = scale
    gsap.to(this.body.scale, { x: scale, y: scale, z: scale, duration, delay, ease: scale > 0.01 ? "elastic.out(1, 0.6)" : "power2.in" })
  }

  setUncertainty(value, { duration = 1.5 } = {}) {
    this.uncertainty = value
    gsap.to(this.halo.scale, { x: 0.6 + value * 1.2, y: 0.6 + value * 1.2, z: 0.6 + value * 1.2, duration, ease: "power2.inOut" })
    gsap.to(this.halo.material, { opacity: 0.25 + value * 1.1, duration })
  }

  setClean(amount) {
    // 0 = dunkelgrau, 1 = fast weiß
    const target = this.baseColor.clone().lerp(new THREE.Color(COLORS.smokeClean), amount)
    gsap.to(this.material.color, { r: target.r, g: target.g, b: target.b, duration: 1.5 })
  }

  // In der Nahansicht einer Station tritt die Wolke in den Hintergrund
  setFaded(faded) {
    gsap.to(this.material, { opacity: faded ? 0.18 : 0.92, duration: 0.8 })
    gsap.to(this.halo.material, { opacity: faded ? 0.1 : 0.25 + this.uncertainty * 1.1, duration: 0.8 })
    this.body.children.forEach((child) => (child.castShadow = !faded))
  }

  update(time) {
    this.group.position.y = this.baseHeight + Math.sin(time * 0.8 + this.phase) * 0.25
    this.body.rotation.y = time * 0.05 + this.phase
    this.halo.rotation.y = -time * 0.3
  }
}
